
import React, { useState, ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { useBoat, BoatType } from "@/context/BoatContext";
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { v4 as uuidv4 } from "uuid";
import { Image, Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface BoatFormProps {
  boat?: BoatType;
  onComplete: () => void;
}

const BoatForm = ({ boat, onComplete }: BoatFormProps) => {
  const { t } = useTranslation("boats");
  const { addBoat, updateBoat } = useBoat();
  const isEditing = !!boat;
  const [imagePreview, setImagePreview] = useState<string | null>(boat?.image || null);

  const form = useForm<Omit<BoatType, "id" | "customFields">>({
    defaultValues: boat ? {
      name: boat.name,
      model: boat.model,
      year: boat.year,
      length: boat.length,
      width: boat.width,
      draft: boat.draft,
      engine: boat.engine,
      engineHours: boat.engineHours || 0,
      waterCapacity: boat.waterCapacity,
      fuelCapacity: boat.fuelCapacity,
      image: boat.image,
    } : {
      name: "",
      model: "",
      year: new Date().getFullYear(),
      length: 0,
      width: 0,
      draft: 0,
      engine: "",
      engineHours: 0,
      waterCapacity: 0,
      fuelCapacity: 0,
      image: "",
    }
  });

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dans un environnement réel, nous téléchargerions l'image sur un serveur
      // Pour cette démo, nous utilisons une URL d'objet local
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
      form.setValue("image", imageUrl);
    }
  };

  const clearImage = () => {
    setImagePreview(null);
    form.setValue("image", "");
  };

  const onSubmit = (data: Omit<BoatType, "id" | "customFields">) => {
    if (isEditing && boat) {
      updateBoat(boat.id, data);
      toast({
        title: t("boatForm.toastUpdatedTitle"),
        description: t("boatForm.toastUpdatedDescription", { name: data.name }),
      });
    } else {
      const newBoat: BoatType = {
        ...data,
        id: uuidv4(),
      };
      addBoat(newBoat);
      toast({
        title: t("boatForm.toastAddedTitle"),
        description: t("boatForm.toastAddedDescription", { name: data.name }),
      });
    }
    onComplete();
  };

  const boatModels = [
    "Oceanic 42", 
    "SailMaster 38", 
    "Navigator 45", 
    "Bénéteau Oceanis 40", 
    "Dufour Grand Large 460", 
    "Jeanneau Sun Odyssey 410",
    "Bavaria C42", 
    "Lagoon 42", 
    "Fountaine Pajot Elba 45",
    "Princess F55", 
    "Sunseeker Manhattan 68",
    "Azimut 50", 
    "Grand Banks 60"
  ];

  // Fonction pour créer les options de la liste déroulante
  const createModelOptions = () => {
    return boatModels.map((model) => (
      <option key={model} value={model}>{model}</option>
    ));
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div className="flex justify-center mb-4">
            <div className="relative w-full max-w-md">
              {imagePreview ? (
                <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src={imagePreview} 
                    alt={t("boatForm.photoAlt")} 
                    className="w-full h-full object-cover"
                  />
                  <button 
                    type="button"
                    onClick={clearImage}
                    className="absolute top-2 right-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full p-1"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Image className="w-10 h-10 mb-3 text-gray-400" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">{t("boatForm.clickToUpload")}</span> {t("boatForm.orDragDrop")}
                    </p>
                    <p className="text-xs text-gray-500">{t("boatForm.photoHint")}</p>
                  </div>
                  <input 
                    type="file" 
                    className="hidden" 
                    onChange={handleImageChange} 
                    accept="image/*"
                  />
                </label>
              )}
            </div>
          </div>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.nameLabel")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("boatForm.namePlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="model"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.modelLabel")}</FormLabel>
                <FormControl>
                  <select 
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    {...field}
                  >
                    <option value="">{t("boatForm.modelPlaceholder")}</option>
                    {createModelOptions()}
                    <option value="custom">{t("boatForm.modelCustomOption")}</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="year"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.yearLabel")}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="engine"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.engineLabel")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("boatForm.enginePlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="engineHours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.engineHoursLabel")}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder={t("boatForm.engineHoursPlaceholder")}
                    {...field} 
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="length"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.lengthLabel")}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.1"
                    {...field} 
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="width"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.widthLabel")}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.1"
                    {...field} 
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="draft"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.draftLabel")}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.1"
                    {...field} 
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="waterCapacity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.waterCapacityLabel")}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fuelCapacity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("boatForm.fuelCapacityLabel")}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onComplete}>
            {t("boatForm.cancel")}
          </Button>
          <Button type="submit" className="bg-nautic-gold hover:bg-nautic-lightgold text-nautic">
            {isEditing ? t("boatForm.updateBoat") : t("boatForm.addBoat")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BoatForm;
